// Draft mocks: synthetic Activities and Origin-Destination results for registry targets whose
// backend fix has not shipped (`pending` in config/products.ts). No target is pending today (the Estonian 1 km grid went live);
// the mechanism is kept for the next one, and its tests mark the EST grid pending (src/test/pendingTarget.ts).
// This is the one module that knows REACT_APP_DRAFT_MOCKS. With the flag off nothing here runs.
//
// The services call in at their single choke point (runActivitiesJob / runOdmJob) and
// fetchAvailability. Everything is deterministic: the rows are seeded from the requested ids and
// the day, so the same request always gives the same answer. The rows have the shape the real API
// sends and go through the same normalisers as real rows.
import type { CountryCode } from '../config/countries';
import { DRAFT_FALLBACK_DELIVERY, capability, pendingFor } from '../config/products';
import { addDays, isIsoDay, parseBucket } from '../utils/dateWindow';
import { readShape } from '../geo/areaShapes';
import { wktAreaKm2 } from '../utils/geo';
import type { IsoDay } from '../utils/dateWindow';
import type { JobOutcome } from './dataJobs';
import { normalizeActivityRow } from './activitiesApi';
import type { ActivitiesJobParams, ActivityRow } from './activitiesApi';
import { normalizeODMResult } from './odmApi';
import type { ODMJobParams, ODMResult } from './odmApi';

/** Days the mock reports as delivered, ending at the latest real delivery. */
export const DRAFT_AVAILABILITY_DAYS = 14;
/** The preview size of the real API; a mock preview is cut at the same length. */
const PREVIEW_ROWS = 100;
/** Other ends invented for one anchor cell when a trip end is "anywhere". */
const ANYWHERE_PARTNERS = 10;

const HOUR_MS = 3_600_000;

/** Read in this one place. A function, not a constant, so tests can switch it per case. */
export const draftMocksEnabled = (): boolean => process.env.REACT_APP_DRAFT_MOCKS === '1';

/** True when the flag is on and the target is a pending registry entry. */
export function isDraftMocked(
  country: CountryCode | null | undefined,
  product: 'activities' | 'odm',
  geoType: string | null | undefined,
): boolean {
  return draftMocksEnabled() && pendingFor(country, product, geoType) !== undefined;
}

// ── Deterministic randomness ──────────────────────────────────────────────────────────────────

function hash(text: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** A generator of numbers in [0, 1) that always gives the same sequence for the same seed text. */
function rng(seed: string): () => number {
  let a = hash(seed);
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Busier in the day than at night, for the hourly shape. */
const dayShape = (hour: number): number => 0.35 + 0.65 * Math.max(0, Math.sin(((hour - 5) / 17) * Math.PI));

// ── The window of a request ───────────────────────────────────────────────────────────────────

interface Bucket {
  /** The row's time_bucket as the API sends it: an hour (ISO) on hourly rows, the day on daily rows. */
  bucket: string;
  hour: number;
}

/**
 * The delivery day a request window belongs to. A window starts at local midnight of the day, so
 * it can start on the evening before in UTC; half a day later is inside the day in every zone.
 */
function windowDay(startTime: string): IsoDay {
  const ms = parseBucket(startTime);
  return ms === null ? '' : new Date(ms + 12 * HOUR_MS).toISOString().slice(0, 10);
}

function bucketsFor(p: { start_time: string; end_time: string; activity_type: 'daily' | 'hourly' }): { day: IsoDay; buckets: Bucket[] } {
  const day = windowDay(p.start_time);
  if (p.activity_type === 'daily') return { day, buckets: [{ bucket: day, hour: 12 }] };
  const start = parseBucket(p.start_time);
  const end = parseBucket(p.end_time);
  if (start === null || end === null) return { day, buckets: [] };
  const buckets: Bucket[] = [];
  for (let t = Math.ceil(start / HOUR_MS) * HOUR_MS; t <= end && buckets.length < 25; t += HOUR_MS) {
    buckets.push({ bucket: new Date(t).toISOString(), hour: new Date(t).getUTCHours() });
  }
  return { day, buckets };
}

// ── Activities ────────────────────────────────────────────────────────────────────────────────

/** The raw rows the real API would send for an Activities request (before normalising). */
export function draftActivityRawRows(params: ActivitiesJobParams): Array<Record<string, unknown>> {
  const { day, buckets } = bucketsFor(params);
  const rows: Array<Record<string, unknown>> = [];
  for (const id of params.geo_ids) {
    const geoId = String(id);
    const cell = rng(`activities|${geoId}`)();
    for (const b of buckets) {
      const r = rng(`activities|${geoId}|${b.bucket}|${params.measure}`);
      const people = Math.round((40 + cell * 900) * (params.activity_type === 'hourly' ? dayShape(b.hour) : 3) * (0.85 + r() * 0.3));
      const value = params.measure === 'stays_dwell_minutes' ? people * Math.round(20 + r() * 40) : people;
      rows.push({
        geo_id: geoId,
        geo_name: null,
        batch_date: day,
        time_bucket: b.bucket,
        type: params.geo_type,
        [params.measure]: value,
      });
    }
  }
  return rows.slice(0, PREVIEW_ROWS);
}

export function draftActivitiesJob(params: ActivitiesJobParams): JobOutcome<ActivityRow> {
  const rows: ActivityRow[] = [];
  for (const raw of draftActivityRawRows(params)) {
    const row = normalizeActivityRow(raw, params.measure);
    if (row !== null) rows.push(row);
  }
  return { kind: 'rows', jobId: `draft-act-${hash(JSON.stringify(params)).toString(16)}`, rows };
}

// ── Origin-Destination ────────────────────────────────────────────────────────────────────────

const CELL = /^(.*?N)(\d+)(E)(\d+)$/;

/** Cells near an anchor, to stand in for "anywhere": same id shape, shifted by up to two cells. */
function partnersOf(anchor: string, count: number): string[] {
  const m = CELL.exec(anchor);
  const r = rng(`partners|${anchor}`);
  const out: string[] = [];
  const seen = new Set<string>();
  for (let guard = 0; out.length < count && guard < 200; guard++) {
    const dn = Math.floor(r() * 5) - 2;
    const de = Math.floor(r() * 5) - 2;
    if (dn === 0 && de === 0) continue;
    const id = m ? `${m[1]}${Number(m[2]) + dn}${m[3]}${Number(m[4]) + de}` : `${anchor}~${dn}:${de}`;
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}

/** The (origin, destination) pairs a request names. An empty side is "anywhere" (see ANYWHERE in utils/odmDirection.ts). */
function pairsOf(params: ODMJobParams): Array<[string, string]> {
  const origins = params.origin_geo_ids.map(String);
  const destinations = params.destination_geo_ids.map(String);
  const pairs: Array<[string, string]> = [];
  if (origins.length > 0 && destinations.length > 0) {
    for (const o of origins) for (const d of destinations) pairs.push([o, d]);
  } else if (origins.length > 0) {
    for (const o of origins) for (const d of partnersOf(o, ANYWHERE_PARTNERS)) pairs.push([o, d]);
  } else {
    for (const d of destinations) for (const o of partnersOf(d, ANYWHERE_PARTNERS)) pairs.push([o, d]);
  }
  return pairs;
}

/** Straight-line metres between two cell ids when both read as 1 km grid cells; otherwise unknown. */
function cellDistance(a: string, b: string): number | null {
  const ma = CELL.exec(a);
  const mb = CELL.exec(b);
  if (!ma || !mb) return null;
  return Math.hypot(Number(ma[2]) - Number(mb[2]), Number(ma[4]) - Number(mb[4])) * 1000;
}

/** The raw rows the real API would send for an Origin-Destination request (before normalising). */
export function draftOdmRawRows(params: ODMJobParams): Array<Record<string, unknown>> {
  const { day, buckets } = bucketsFor(params);
  const rows: Array<Record<string, unknown>> = [];
  for (const [origin, destination] of pairsOf(params)) {
    const pair = rng(`odm|${origin}|${destination}`)();
    const metres = cellDistance(origin, destination);
    for (const b of buckets) {
      const r = rng(`odm|${origin}|${destination}|${b.bucket}`);
      const trips = Math.round((12 + pair * 280) * (params.activity_type === 'hourly' ? dayShape(b.hour) : 4) * (0.85 + r() * 0.3));
      const distance = Math.round(metres === null ? 800 + pair * 14000 : metres * (0.95 + r() * 0.2));
      rows.push({
        batch_date: day,
        time_bucket: b.bucket,
        origin_geo_id: origin,
        destination_geo_id: destination,
        trips,
        General_Distance: distance,
        Average_Speed: Math.round((12 + pair * 40) * 10) / 10,
      });
    }
  }
  return rows.slice(0, PREVIEW_ROWS);
}

export function draftOdmJob(params: ODMJobParams): JobOutcome<ODMResult> {
  const rows = draftOdmRawRows(params).map(normalizeODMResult);
  return { kind: 'rows', jobId: `draft-odm-${hash(JSON.stringify(params)).toString(16)}`, rows };
}

// ── Availability ──────────────────────────────────────────────────────────────────────────────

const asDay = ([y, m, d]: readonly [number, number, number]): IsoDay =>
  `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

/**
 * The /data-availability rows with draft rows added: for every pending table of `country`, the
 * last DRAFT_AVAILABILITY_DAYS days ending at the latest real delivery in `real`. With no real
 * rows the registry's fallback delivery days stand in for them. Flag off or no pending table: `real`.
 */
export function withDraftAvailability(country: CountryCode, real: unknown[]): unknown[] {
  if (!draftMocksEnabled()) return real;
  const tables: Array<{ dataset: string; table: string }> = [];
  for (const product of ['activities', 'odm'] as const) {
    const cap = capability(country, product);
    for (const geo of cap?.geo ?? []) {
      if (!geo.pending) continue;
      for (const table of Object.values(geo.tables)) if (table) tables.push({ dataset: cap!.dataset, table });
    }
  }
  if (tables.length === 0) return real;

  let latest: IsoDay | null = null;
  const consider = (day: unknown) => {
    if (isIsoDay(day) && (latest === null || day > latest)) latest = day;
  };
  for (const row of real) {
    if (row === null || typeof row !== 'object') continue;
    const r = row as Record<string, unknown>;
    if (String(r.country_code ?? '').toUpperCase() !== country) continue;
    const raw = r.batch_date;
    consider(raw !== null && typeof raw === 'object' && 'value' in raw ? (raw as { value: unknown }).value : raw);
  }
  if (latest === null) for (const d of DRAFT_FALLBACK_DELIVERY[country] ?? []) consider(asDay(d));
  if (latest === null) return real;

  const end: IsoDay = latest;
  const added: unknown[] = [];
  for (let back = 0; back < DRAFT_AVAILABILITY_DAYS; back++) {
    const day = addDays(end, -back);
    for (const { dataset, table } of tables) {
      added.push({
        country_code: country,
        dataset,
        table_name: table,
        batch_date: day,
        total_rows: 1000,
        last_modified_time: `${day}T12:00:00Z`,
      });
    }
  }
  return [...real, ...added];
}

// ── Exposure ("Population baseline") ────────────────────────────────────────────────────────────

/**
 * The exposure layer answers from the open data API. The mock is used ONLY when
 * REACT_APP_DRAFT_MOCKS=1 is set explicitly: a prod or plain build never invents numbers. With no
 * open data API and no flag the feature is hidden instead (openDataAvailable in config/openData.ts).
 */
export const exposureMockEnabled = (): boolean => draftMocksEnabled();

/** Plausible for Norway: residents per km² and workplace employees per km². */
const RESIDENT_DENSITY = [2000, 6000] as const;
const WORKER_DENSITY = [600, 2800] as const;

/**
 * Residents and workers for some geometries (WKT), seeded from the geometry text: the same area
 * always gives the same answer. Each geometry gets its own densities; the totals are summed.
 */
export function draftExposureBase(geometries: readonly string[]): { residents: number; workers: number } {
  let residents = 0;
  let workers = 0;
  for (const wkt of geometries) {
    const r = rng(`exposure|${wkt}`);
    const km2 = wktAreaKm2(wkt);
    residents += km2 * (RESIDENT_DENSITY[0] + r() * (RESIDENT_DENSITY[1] - RESIDENT_DENSITY[0]));
    workers += km2 * (WORKER_DENSITY[0] + r() * (WORKER_DENSITY[1] - WORKER_DENSITY[0]));
  }
  return { residents: Math.round(residents), workers: Math.round(workers) };
}

// ── Population baseline cells (250 m grid) ────────────────────────────────────────────────────

export type BaselineMeasureId = 'residents' | 'employees';

export interface DraftBaselineCell {
  ssbid: number;
  value: number;
  geometry: { type: 'Polygon'; coordinates: number[][][] };
}

type Pt = [number, number];

function pointInRing(x: number, y: number, ring: Pt[]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

function segmentsCross(a: Pt, b: Pt, c: Pt, d: Pt): boolean {
  const o = (p: Pt, q: Pt, r: Pt) => Math.sign((q[0] - p[0]) * (r[1] - p[1]) - (q[1] - p[1]) * (r[0] - p[0]));
  return o(a, b, c) !== o(a, b, d) && o(c, d, a) !== o(c, d, b);
}

/** Whether the rectangle shares any area with the polygon (outer ring first, then holes). */
function rectTouchesPolygon(x0: number, y0: number, x1: number, y1: number, rings: Pt[][]): boolean {
  const inPolygon = (x: number, y: number) =>
    pointInRing(x, y, rings[0]) && !rings.slice(1).some((hole) => pointInRing(x, y, hole));
  if ([[x0, y0], [x1, y0], [x1, y1], [x0, y1]].some(([x, y]) => inPolygon(x, y))) return true;
  const corners: Pt[] = [[x0, y0], [x1, y0], [x1, y1], [x0, y1]];
  for (const ring of rings) {
    for (let i = 0; i < ring.length; i++) {
      const p = ring[i];
      if (p[0] >= x0 && p[0] <= x1 && p[1] >= y0 && p[1] <= y1) return true;
      const q = ring[(i + 1) % ring.length];
      for (let k = 0; k < 4; k++) if (segmentsCross(p, q, corners[k], corners[(k + 1) % 4])) return true;
    }
  }
  return false;
}

/** The most grid cells the bounding box of a request may span before it is called too large without trying. */
const MAX_BBOX_CELLS = 60000;

/**
 * 250 m square cells over the bounding box of some areas (WKT), keeping those that touch an area.
 * The cell size in degrees is worked out at the latitude of the area, which is close enough for a
 * draft. Values are seeded from the cell id and the measure, so the same area always gives the
 * same cells. Returns null when more than `cap` cells would be drawn.
 */
export function draftBaselineCells(
  geometries: readonly string[],
  measure: BaselineMeasureId,
  cellMetres: number,
  cap: number,
): DraftBaselineCell[] | null {
  const polygons: Pt[][][] = [];
  for (const wkt of geometries) {
    const g = readShape(wkt);
    if (g?.type === 'Polygon') polygons.push(g.coordinates);
    else if (g?.type === 'MultiPolygon') polygons.push(...g.coordinates);
  }
  if (polygons.length === 0) return [];

  let minLng = Infinity, maxLng = -Infinity, minLat = Infinity, maxLat = -Infinity;
  for (const rings of polygons) {
    for (const [lng, lat] of rings[0]) {
      minLng = Math.min(minLng, lng);
      maxLng = Math.max(maxLng, lng);
      minLat = Math.min(minLat, lat);
      maxLat = Math.max(maxLat, lat);
    }
  }
  const dLat = cellMetres / 111320;
  const dLng = dLat / Math.cos(((minLat + maxLat) / 2) * (Math.PI / 180));
  const ix0 = Math.floor(minLng / dLng);
  const ix1 = Math.floor(maxLng / dLng);
  const iy0 = Math.floor(minLat / dLat);
  const iy1 = Math.floor(maxLat / dLat);
  if ((ix1 - ix0 + 1) * (iy1 - iy0 + 1) > MAX_BBOX_CELLS) return null;

  const maxValue = measure === 'residents' ? 450 : 260;
  const cells: DraftBaselineCell[] = [];
  for (let iy = iy0; iy <= iy1; iy++) {
    for (let ix = ix0; ix <= ix1; ix++) {
      const x0 = ix * dLng, x1 = x0 + dLng, y0 = iy * dLat, y1 = y0 + dLat;
      if (!polygons.some((rings) => rectTouchesPolygon(x0, y0, x1, y1, rings))) continue;
      if (cells.length >= cap) return null;
      const ssbid = (iy + 1000000) * 10000000 + (ix + 1000000);
      const r = rng(`cell|${measure}|${ssbid}`)();
      cells.push({
        ssbid,
        value: 1 + Math.round(r * r * maxValue),
        geometry: { type: 'Polygon', coordinates: [[[x0, y0], [x1, y0], [x1, y1], [x0, y1], [x0, y0]]] },
      });
    }
  }
  return cells;
}
